import axiosConfig from "./axiosConfig";

// One rule in the operator-editable landlord-research prompt. Mirrors the BMA
// PromptRule (snake_case on the wire - the addendum round-trips as its model dump).
// `improvement` is operator-only documentation and is never sent to the model.
export interface PromptRuleModel {
  rule_id: string;
  category: string;
  title: string;
  guidance: string;
  why: string;
  limits: string;
  evidence: string;
  improvement: string;
  status: "active" | "draft" | "retired";
  position: number;
}

// The whole editable prompt: a free-text preamble plus an ordered rule set.
// `prompt_text` (on the revision) is the compiled render of this.
export interface PromptAddendumModel {
  preamble: string;
  rules: PromptRuleModel[];
  next_rule_seq: number;
}

// One revision of the operator-editable landlord-research prompt. The built-in
// seed is revision 1 (isSeed=true, no DB row); operator saves climb from 2.
export interface LandlordPromptRevision {
  revisionNumber: number;
  // The compiled render (what the agent runs on); `addendum` is its source.
  promptText: string;
  addendum: PromptAddendumModel;
  ruleCount: number;
  // Human summary of what this revision changed (auto-derived for per-rule edits).
  note: string | null;
  createdBy: string;
  createdAt: string | null;
  isActive: boolean;
  isSeed?: boolean;
}

export interface LandlordPromptResponse {
  // The revision the research agent currently runs (a saved one, or the seed).
  active: LandlordPromptRevision;
  // The shipped built-in prompt text, for a "reset to default" action.
  defaultSeed: string;
  // The shipped built-in prompt as an addendum (whole text as the preamble, no
  // rules), for resetting the structured editor.
  defaultSeedAddendum: PromptAddendumModel;
  // The non-editable data-residency guardrail appended to whatever prompt is
  // active. Returned so the card can show it read-only.
  enforcedFooter: string;
  // Full history, newest first, ending with the synthetic seed (revision 1).
  revisions: LandlordPromptRevision[];
}

const getLandlordResearchPrompt = async (
  authToken: string
): Promise<LandlordPromptResponse> => {
  const { data } = await axiosConfig.get(`/leads/landlord-research-prompt`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default getLandlordResearchPrompt;
